
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNotification } from '@/contexts/NotificationContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

export const TenderSubscriptions = () => {
  const { subscriptions } = useNotification();
  const { t } = useTranslation();
  
  // Fetch user profile data to get preferences
  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile-dashboard'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
        
      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
      
      return data;
    }
  });

  // Check if there are any preferences set
  const hasIndustries = profileData?.preferred_industries?.length > 0;
  const hasCountries = profileData?.preferred_countries?.length > 0;
  const hasPreferences = hasIndustries || hasCountries;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-5">
        {!hasPreferences ? (
          <div className="text-center py-6 text-muted-foreground">
            <p>{t('dashboard.subscriptions.noSubscriptions', 'Du har ingen aktive abonnementer. Gå til din profil for at tilføje dine præferencer.')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">{t('dashboard.subscriptions.industries', "Brancher")}</h3>
              <div className="flex flex-wrap gap-2">
                {hasIndustries ? (
                  profileData.preferred_industries.map((industry, index) => (
                    <Badge key={`industry-${index}`} variant="outline">
                      {industry}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2">{t('dashboard.subscriptions.countries', "Lande")}</h3>
              <div className="flex flex-wrap gap-2">
                {hasCountries ? (
                  profileData.preferred_countries.map((country, index) => (
                    <Badge key={`country-${index}`} variant="outline">
                      {country}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </div>
            </div>

            <div className="pt-2">
              <Button variant="outline" size="sm" className="w-full" asChild>
                <Link to="/profile">
                  {t('dashboard.subscriptions.edit', "Rediger")}
                </Link>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TenderSubscriptions;
